/**
 * DataQualityLauncher — 3-step wizard to launch a data-quality profile run.
 * Phase 4.10 β5 — WQ-240.
 *
 * Steps:
 *   1. Pick a catalog entry (DataCatalog dataset)
 *   2. Options — set_baseline checkbox + 5-dimension preview
 *   3. Review & Launch
 *
 * Wraps existing POST /api/admin/ai/data-quality/profile (catalog_entry_id +
 * set_baseline). Backend computes all 5 quality dimensions automatically.
 *
 * Pattern reuse: WQ-237 AutoMLWizard + WQ-238 PredictionRequestForm
 * (copy-paste-with-divergence per WQ-237 NOTES).
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { listAvailableDatasets, type DatasetOption } from '../../services/datasetService';
import { runProfile, type QualityProfile } from '../../services/dataQualityService';
import WizardModalShell from '../wizard/WizardModalShell';

type WizardStep = 1 | 2 | 3;

interface DataQualityLauncherProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitted?: (profileId: string) => void;
}

const STEP_TITLES: Record<WizardStep, string> = {
  1: 'Choose a catalog dataset',
  2: 'Configure profile run',
  3: 'Review & launch',
};

const DIMENSIONS = [
  { key: 'completeness', label: 'Completeness', hint: 'non-null rate' },
  { key: 'accuracy', label: 'Accuracy', hint: 'value range / type validity' },
  { key: 'consistency', label: 'Consistency', hint: 'cross-column rules' },
  { key: 'timeliness', label: 'Timeliness', hint: 'freshness of timestamps' },
  { key: 'uniqueness', label: 'Uniqueness', hint: 'distinct vs duplicate rate' },
] as const;

const DataQualityLauncher: React.FC<DataQualityLauncherProps> = ({ isOpen, onClose, onSubmitted }) => {
  const { authFetch } = useAuth();
  const [step, setStep] = useState<WizardStep>(1);

  // Step 1 state
  const [catalogDatasets, setCatalogDatasets] = useState<DatasetOption[]>([]);
  const [datasetsLoading, setDatasetsLoading] = useState(false);
  const [datasetsError, setDatasetsError] = useState<string | null>(null);
  const [selectedDataset, setSelectedDataset] = useState<DatasetOption | null>(null);

  // Step 2 state
  const [setBaseline, setSetBaseline] = useState<boolean>(false);

  // Submit state
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);

  // Reset wizard each time it opens
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setSelectedDataset(null);
      setSetBaseline(false);
      setLaunchError(null);
    }
  }, [isOpen]);

  // Load datasets when wizard opens
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      setDatasetsLoading(true);
      setDatasetsError(null);
      try {
        const data = await listAvailableDatasets(authFetch);
        if (cancelled) return;
        // DQ profiles operate on catalog entries (catalog_entry_id), not composed datasets.
        setCatalogDatasets(data.catalog ?? []);
      } catch (err) {
        if (!cancelled) setDatasetsError(err instanceof Error ? err.message : 'Failed to load datasets');
      } finally {
        if (!cancelled) setDatasetsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, authFetch]);

  const canAdvance = useMemo(() => {
    if (step === 1) return !!selectedDataset;
    if (step === 2) return true;
    return false;
  }, [step, selectedDataset]);

  const handleNext = () => {
    if (!canAdvance) return;
    setStep((prev) => (prev < 3 ? ((prev + 1) as WizardStep) : prev));
  };

  const handleBack = () => {
    setStep((prev) => (prev > 1 ? ((prev - 1) as WizardStep) : prev));
  };

  const handleLaunch = async () => {
    if (!selectedDataset) return;
    setLaunching(true);
    setLaunchError(null);
    try {
      const profile: QualityProfile = await runProfile(authFetch, {
        catalog_entry_id: selectedDataset.id,
        set_baseline: setBaseline,
      });
      if (onSubmitted) onSubmitted(profile.id);
      onClose();
    } catch (err) {
      setLaunchError(err instanceof Error ? err.message : 'Failed to launch quality profile');
    } finally {
      setLaunching(false);
    }
  };

  return (
    <WizardModalShell
      isOpen={isOpen}
      onClose={onClose}
      title="Run Data Quality Profile"
      totalSteps={3}
      currentStep={step}
      stepTitle={STEP_TITLES[step]}
      testIdPrefix="dq-launcher"
      canAdvance={canAdvance}
      submitting={launching}
      onBack={handleBack}
      onNext={handleNext}
      onSubmit={handleLaunch}
      launchError={launchError}
      maxWidthPx={640}
    >
      <>
          {step === 1 && (
            <div data-testid="dq-launcher-step-1">
              {datasetsLoading && (
                <div data-testid="dq-launcher-datasets-loading" style={{ color: 'var(--text-secondary)' }}>
                  Loading catalog datasets...
                </div>
              )}
              {datasetsError && (
                <div data-testid="dq-launcher-datasets-error" style={{
                  padding: 12, background: '#fef2f2', color: '#dc2626', borderRadius: 8, marginBottom: 12,
                }}>
                  {datasetsError}
                </div>
              )}
              {!datasetsLoading && !datasetsError && catalogDatasets.length === 0 && (
                <div data-testid="dq-launcher-empty-datasets" style={{
                  padding: 20, textAlign: 'center', color: 'var(--text-secondary)',
                  border: '1px dashed var(--border)', borderRadius: 8,
                }}>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>No catalog datasets available</div>
                  <div style={{ fontSize: 13 }}>
                    Register a dataset in Data Sources first.
                  </div>
                </div>
              )}
              {catalogDatasets.length > 0 && (
                <div data-testid="dq-launcher-catalog-list">
                  {catalogDatasets.map((d) => (
                    <button
                      key={d.id}
                      data-testid={`dq-launcher-dataset-${d.id}`}
                      onClick={() => setSelectedDataset(d)}
                      style={{
                        display: 'block', width: '100%', textAlign: 'left',
                        padding: '8px 12px', borderRadius: 6, marginBottom: 4,
                        border: selectedDataset?.id === d.id ? '1px solid var(--accent-primary)' : '1px solid var(--border)',
                        background: selectedDataset?.id === d.id ? 'rgba(59,130,246,0.05)' : 'var(--bg-secondary)',
                        color: 'var(--text-primary)', cursor: 'pointer', fontSize: 13,
                      }}
                    >
                      <span style={{ fontWeight: 600 }}>{d.name}</span>
                      <span style={{ fontSize: 11, color: 'var(--text-secondary)', marginLeft: 8 }}>(catalog)</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div data-testid="dq-launcher-step-2">
              <div style={{ marginBottom: 16, fontSize: 13, color: 'var(--text-secondary)' }}>
                The profile will compute scores for the following 5 dimensions:
              </div>
              <div data-testid="dq-launcher-dimension-list" style={{ marginBottom: 20 }}>
                {DIMENSIONS.map((d) => (
                  <div
                    key={d.key}
                    data-testid={`dq-launcher-dimension-${d.key}`}
                    style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '8px 12px', borderRadius: 6, marginBottom: 4,
                      border: '1px solid var(--border)', background: 'var(--bg-secondary)',
                    }}
                  >
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{d.label}</span>
                    <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{d.hint}</span>
                  </div>
                ))}
              </div>
              <label
                data-testid="dq-launcher-baseline-toggle-label"
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                  borderRadius: 6, cursor: 'pointer',
                  border: '1px solid var(--border)', background: 'var(--bg-secondary)',
                }}
              >
                <input
                  type="checkbox"
                  data-testid="dq-launcher-baseline-toggle"
                  checked={setBaseline}
                  onChange={(e) => setSetBaseline(e.target.checked)}
                />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>Set as baseline</div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                    Mark this profile as the reference for future drift checks.
                  </div>
                </div>
              </label>
            </div>
          )}

          {step === 3 && selectedDataset && (
            <div data-testid="dq-launcher-step-3">
              <div style={{
                padding: 16, borderRadius: 8, border: '1px solid var(--border)',
                background: 'var(--bg-secondary)', fontSize: 13, lineHeight: 1.6,
              }}>
                <div data-testid="dq-launcher-review-summary">
                  Run a quality profile on dataset <strong>{selectedDataset.name}</strong>.
                  <br />
                  Dimensions assessed: <strong>5</strong> (completeness, accuracy, consistency, timeliness, uniqueness).
                  <br />
                  Set as baseline: <strong>{setBaseline ? 'Yes' : 'No'}</strong>.
                </div>
              </div>
            </div>
          )}
      </>
    </WizardModalShell>
  );
};

export default DataQualityLauncher;
